import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import Rocket from "@/components/Rocket";
import { Play, Pause, RotateCcw, X, Rocket as RocketIcon } from "lucide-react";

const audioCache = {};
function playClip(name) {
  try {
    let a = audioCache[name];
    if (!a) { a = new Audio(`/audio/countdown/${name}.mp3`); audioCache[name] = a; }
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch {}
}

// Teacher mission timer — top pop-up. mm:ss set, start/pause/reset, spoken last-10s
// Apollo countdown + rocket lift-off at zero. `accent` themes it to the project colour.
export default function MissionTimer({ open, onClose, accent = "#3B82F6" }) {
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [remaining, setRemaining] = useState(null); // seconds left when running
  const [running, setRunning] = useState(false);
  const [launched, setLaunched] = useState(false);
  const tickRef = useRef(null);
  const spokenRef = useRef(new Set());

  const clearTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };

  useEffect(() => () => clearTick(), []);

  useEffect(() => {
    if (!running) return;
    clearTick();
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r === null) return r;
        const next = r - 1;
        if (next <= 10 && next >= 1 && !spokenRef.current.has(next)) {
          spokenRef.current.add(next);
          playClip(String(next));
        }
        if (next <= 0) {
          clearTick();
          setRunning(false);
          playClip("liftoff");
          setLaunched(true);
          setTimeout(() => setLaunched(false), 4200);
          return 0;
        }
        return next;
      });
    }, 1000);
    return clearTick;
  }, [running]);

  if (!open) return null;

  const start = () => {
    const total = remaining !== null && remaining > 0 ? remaining : minutes * 60 + seconds;
    if (total <= 0) return;
    spokenRef.current = new Set();
    setRemaining(total);
    setRunning(true);
  };
  const startPreset = (mins) => {
    spokenRef.current = new Set();
    setMinutes(mins);
    setSeconds(0);
    setRemaining(mins * 60);
    setRunning(true);
  };
  const pause = () => { setRunning(false); clearTick(); };
  const reset = () => { setRunning(false); clearTick(); setRemaining(null); setLaunched(false); spokenRef.current = new Set(); };

  const shown = remaining !== null ? remaining : minutes * 60 + seconds;
  const mm = String(Math.floor(shown / 60)).padStart(2, "0");
  const ss = String(shown % 60).padStart(2, "0");
  const inCountdown = running && remaining !== null && remaining <= 10;

  return (
    <>
      <div
        data-testid="mission-timer-popup"
        className="timer-pop fixed top-3 left-1/2 -translate-x-1/2 z-[80] w-[92%] max-w-md rounded-2xl border-2 bg-[#0E1126]/95 backdrop-blur-xl px-4 py-3 shadow-2xl"
        style={{ borderColor: accent, boxShadow: `0 0 34px ${accent}66` }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="font-heading font-bold text-sm tracking-wider flex items-center gap-1" style={{ color: accent }}>
            <RocketIcon className="w-4 h-4" /> Mission Timer
          </span>
          <button data-testid="timer-close-btn" onClick={() => { reset(); onClose(); }} className="text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {remaining === null && !running ? (
          <>
          <div className="flex items-center justify-center gap-2 mb-2" data-testid="timer-setup">
            <input data-testid="timer-minutes-input" type="number" min="0" max="99" value={minutes}
              onChange={(e) => setMinutes(Math.max(0, Math.min(99, Number(e.target.value) || 0)))}
              className="w-16 text-center bg-[#141738] border border-[#252B5C] rounded-lg h-11 text-2xl font-heading text-white" />
            <span className="text-2xl font-heading text-white">:</span>
            <input data-testid="timer-seconds-input" type="number" min="0" max="59" value={seconds}
              onChange={(e) => setSeconds(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
              className="w-16 text-center bg-[#141738] border border-[#252B5C] rounded-lg h-11 text-2xl font-heading text-white" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-3" data-testid="timer-presets">
            <span className="text-xs text-slate-400 mr-1">Quick start:</span>
            {[1, 2, 5, 10].map((m) => (
              <Button key={m} data-testid={`timer-preset-${m}`} onClick={() => startPreset(m)} size="sm" variant="outline" className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] min-w-[42px] font-heading">
                {m}m
              </Button>
            ))}
          </div>
          </>
        ) : (
          <div data-testid="timer-display" className={`text-center font-heading font-bold tabular-nums leading-none mb-3 ${inCountdown ? "timer-alarm text-5xl" : "text-4xl"}`} style={{ color: inCountdown ? "#FF6B35" : accent }}>
            {mm}:{ss}
          </div>
        )}

        <div className="flex items-center justify-center gap-2">
          {!running ? (
            <Button data-testid="timer-start-btn" onClick={start} className="min-h-[42px] font-heading font-bold text-[#0B0D1B]" style={{ backgroundColor: accent }}>
              <Play className="w-4 h-4 mr-1" /> {remaining !== null && remaining > 0 ? "Resume" : "Start"}
            </Button>
          ) : (
            <Button data-testid="timer-pause-btn" onClick={pause} variant="outline" className="min-h-[42px] border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]">
              <Pause className="w-4 h-4 mr-1" /> Pause
            </Button>
          )}
          <Button data-testid="timer-reset-btn" onClick={reset} variant="outline" className="min-h-[42px] border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
      </div>

      {inCountdown && (
        <div data-testid="countdown-overlay" className="fixed inset-0 z-[85] flex items-center justify-center pointer-events-none">
          <span key={remaining} className="countdown-num font-heading font-black text-white" style={{ textShadow: `0 0 60px ${accent}, 0 0 20px ${accent}` }}>
            {remaining}
          </span>
        </div>
      )}

      {launched && (
        <div data-testid="timer-launch" className="fixed inset-0 z-[90] flex flex-col items-center justify-end pointer-events-none overflow-hidden">
          <div className="relative z-10 launch-rise">
            <div className="launch-shake"><Rocket color={accent} size={130} /></div>
          </div>
          <p className="absolute top-1/3 z-10 font-heading text-5xl sm:text-7xl font-black text-white text-center w-full" style={{ textShadow: `0 0 40px ${accent}` }}>
            BLAST OFF! 🚀
          </p>
        </div>
      )}
    </>
  );
}
